import React from "react";
import { Typography, Icon } from "@material-ui/core";
import { makeStyles, useTheme } from "@material-ui/core/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import LazyLoadImage from "../LazyLoadImage/LazyLoadImage";
import SchoolBackgroundImage from "../../assets/mywhanau-school-form-bg.jpg";
import MywhanauLogo from "../../assets/myFamilyHandLogo.png";
import AppleStoreLink from "../../assets/AppleLink.png";
import GooglePlayStoreLink from "../../assets/GooglePlayLink.png";
import LinkIcon from "../../assets/Icons/ExternalLinkIcon";

const useStyles = makeStyles(() => ({
  desktopContainer: {
    display: "flex",
    flexDirection: "row",
  },
  mobileContainer: {
    display: "flex",
    flexDirection: "column",
  },
  imageContainer:{
    width: "600px",
    display: "inline-flex",
    justifyContent: "center",
    "@media (max-width:601px)": {
      width: "100vw",
    },
    "@media (min-width:600px)": {
      width: "100%"
    },
  },
  mainContainer: {
    backgroundColor: "#fdfbf9",
    zIndex: "1",
    position: "relative",
    padding: "32px 16px",
    width: "568px",
    margin: "auto",
    borderTopRightRadius: "25px",
    borderTopLeftRadius: "25px",
    "@media (max-width:601px)": {
      width: "calc(100vw - 32px)",
    },
  },
  container: {
    width: "568px",
    minHeight: "calc(100vh - 303px)",
    display: "flex",
    flexFlow: "column",
    alignItems: "center",
    "& *": {
      fontFamily: "Open Sans",
    },
    "@media (max-width:601px)": {
      width: "calc(100vw - 32px)",
    },
  },
  mobContainer:{
    width: "390px",
    height: "780px",
    display: "flex",
    flexFlow: "column",
    alignItems: "center",
    "& *": {
      fontFamily: "Open Sans",
    },
  },
  mainHeading: {
    fontSize: 24,
    fontWeight: "bold",
    color: "#1d1d1d",
    display: "inline-flex",
    flexFlow: "column",
    marginBottom: "32px",
    alignItems: "center",
    width: "100%",
    textAlign: "center"
  },
  description: {
    fontSize: 18,
    marginBottom: "32px",
    textAlign: "center",
  },
  emailDescription: {
    fontSize: 14,
    marginBottom: "24px",
    textAlign: "center",
  },
  mailTo: {
    textDecoration: "none",
    color: "#1d1d1d",
  },
  appLinks: {
    display: "inline-flex",
    marginBottom: "32px",
    width: "100%",
    justifyContent: "center",
    "@media (max-width:431px)": {
    display: "flex",
    flexFlow: "column",
    alignItems: "center"
    }
  },
  link: {
    "& img": {
      width: "180px",
      height: "52.9px",
    },
    "&:first-child": {
      marginRight: "32px",
    },
    "@media (max-width:431px)": {
      "&:first-child": {
        marginRight: "0px",
        marginBottom: "32px",
      },
    },
  },
  backgroundImage: {
    float: "right",
    width: "100%",
    position: "fixed",
    height: "100%",
    objectFit: "cover",
    opacity: "0.4",
    right: "0px",
    backgroundSize: "cover",
    top: 0,
  },
  mywhanauLogo: {
    position: "relative",
    bottom: "10px",
    width: "200px",
    height: "171.3px",
    objectFit: "contain",
    margin: "40px auto 28px auto",
  },
  mainSection: {
    width: "100%",
    minWidth: "fit-content",
    display: "flex",
    flexFlow: "column",
    background: "black",
    position: "fixed",
    height: "100%"
  },
  redirectLink: {
    height: "24px",
    display: "inline-flex",
    minWidth: "293px",
    justifyContent: "space-between",
    "& *": {
      fontFamily: "Open Sans",
      fontSize: "14px",
      fontWeight: "600",
      color: "#1d1d1d",
      display: "flex",
      alignItems: "center",
      textDecoration: "none",
    },
    "& span": {
      "&:first-child": {
        height: 16,
        width: 16,
        marginRight: "4px",
      },
    },
    "& a":{
      "&:hover":{
        "& span":{
          color: "#8bc517"
        },
        "& g":{
          stroke:"#8bc517"
        }
      }
    }
  },
}));

const Fallback = () => {
  const classes = useStyles();
  const theme = useTheme();
  const mobileMatches = useMediaQuery(theme.breakpoints.down("sm"));

  return (
    <div
      className={
        mobileMatches ? classes.mobileContainer : classes.desktopContainer
      }
    >
      <div
        style={{
          display: "flex",
          flexFlow: "column",
          width: "100%",
          overflow: "auto"
        }}
      >
      <div className={classes.mainSection}>
        <LazyLoadImage
          src={SchoolBackgroundImage}
          alt="error"
          loadingStyle={{
            width: "100%",
            height: "100%",
            radius: "0",
          }}
          className={classes.backgroundImage}
        >
          <div>Fallback</div>
        </LazyLoadImage>
        </div>
        <div className={classes.imageContainer}>
        <img
          src={MywhanauLogo}
          alt="error"
          className={classes.mywhanauLogo}
          loading="lazy"
        />
        </div>
        <div className={classes.mainContainer}>
          <div className={classes.container}>
            <Typography className={classes.mainHeading}>
              <span>Access your</span>
              <span> School Calendar Organiser</span>
            </Typography>
            <Typography className={classes.description}>
              Please ensure you have registered for an account (using the
              registration form provided by your school) and you have received
              confirmation that your account access has been approved.
            </Typography>
            <div className={classes.appLinks}>
              <div className={classes.link}>
                <a
                  target="_blank"
                  rel="noreferrer"
                  href="https://apps.apple.com/au/app/mywh%C4%81nau-calendar-assistant/id1528123918"
                >
                  <img src={AppleStoreLink} alt="apple_store_link" />
                </a>
              </div>
              <div className={classes.link}>
                <a
                  target="_blank"
                  rel="noreferrer"
                  href="https://play.google.com/store/apps/details?id=com.mywhanau.calendar"
                >
                  <img src={GooglePlayStoreLink} alt="google_play_store_link" />
                </a>
              </div>
            </div>
            <Typography className={classes.emailDescription}>
              Need help?{" "}
              <a
                style={{ color: "#1d1d1d", textDecoration: "none" }}
                className={classes.mailTo}
                href="mailto:care@myfamilyhand.com.au"
              >
                care@myfamilyhand.com.au
              </a>
            </Typography>
            <div className={classes.redirectLink}>
              <div>
                <a
                  rel="noreferrer noopener"
                  target="_blank"
                  href="https://www.myfamilyhand.com.au"
                >
                  <Icon><LinkIcon/></Icon>
                  <span>My Family Hand</span>
                </a>
              </div>
              <div>
                <a
                  rel="noreferrer noopener"
                  target="_blank"
                  href="https://www.myfamilyhand.com.au/school-terms-and-conditions"
                >
                  <Icon><LinkIcon/></Icon>
                  <span>Terms</span>
                </a>
              </div>
              <div>
                <a
                  rel="noreferrer noopener"
                  target="_blank"
                  href="https://www.myfamilyhand.com.au/privacy-policy"
                >
                  <Icon><LinkIcon/></Icon>
                  <span>Privacy</span>
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Fallback;
